/*
 * Copyright (c) CIRROM 2026.
 */

import { useEffect, useState } from "react";
import { AccountSummary, getAccountSummary } from "../../../api/accounts";
import { getExplainForSuggestedChange } from "../../../api/suggestedchanges";
import { Loadable, useLoadCycle } from "../../utils/useLoadCycle";

/*
 * Loading an account summary, from either of the two ways in.
 *
 * The page and the navbar know the system and the account up front and can
 * ask for the summary straight away. The suggested-changes grid knows
 * neither: an ACCOUNT row carries a change id, a batch id and the CRM account
 * id (parked under crm_contact_id, one id column serving both entities), but
 * no system - and the page's own route param is a project id on data-project
 * routes, so it cannot stand in. The explain payload is the only place the
 * system id is always right, which is why the grid path waits for it.
 */

export interface AccountSummaryIds {
  integratedSystemId?: number | null;
  crmAccountId?: string | null;
  suggestedChangeId?: number | null;
  batchId?: number | null;
  recordId?: number | null;
}

export const useAccountSummary = (ids: AccountSummaryIds, enabled = true) => {
  const {
    integratedSystemId,
    crmAccountId,
    suggestedChangeId,
    batchId,
    recordId,
  } = ids;

  const [summary, setSummary] = useState<Loadable<AccountSummary>>(undefined);
  /* What the explain payload reported, for callers that supplied neither. */
  const [resolved, setResolved] = useState<{
    systemId?: number;
    crmAccountId?: string;
  }>({});

  const idsKey = `${integratedSystemId}:${crmAccountId}:${suggestedChangeId}:${batchId}:${recordId}`;
  /*
   * Silent reloads (after a write) keep the current card on screen while
   * fresh data arrives; loud ones (navigation, retry) show the loading state.
   */
  const { reloadToken, startLoad, reload, reloadSilently } =
    useLoadCycle(idsKey);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const { showLoading } = startLoad();
    if (showLoading) {
      setSummary(undefined);
      setResolved({});
    }

    const loadSummary = (systemId?: number, accountId?: string | null) => {
      if (!systemId || !accountId) {
        setSummary(null);
        return;
      }
      setResolved({ systemId, crmAccountId: accountId });
      getAccountSummary(systemId, accountId)
        .then((response) => {
          if (!cancelled) setSummary(response);
        })
        .catch(() => {
          if (!cancelled) setSummary(null);
        });
    };

    /* Both ids in hand: this is the page's and the navbar's way in. */
    if (integratedSystemId && crmAccountId) {
      loadSummary(integratedSystemId, crmAccountId);
      return () => {
        cancelled = true;
      };
    }

    /*
     * Nothing identifies an account and no ticket can name one: not a load, a
     * bad address. Settling here rather than fetching preserves the page's
     * bad-URL error path.
     */
    if (!suggestedChangeId) {
      setSummary(null);
      return () => {
        cancelled = true;
      };
    }

    getExplainForSuggestedChange(
      suggestedChangeId,
      batchId ?? null,
      recordId ?? null,
      integratedSystemId ?? null
    )
      .then((response) => {
        if (cancelled) return;
        /*
         * The row's own account id is preferred when the grid supplied one:
         * it is the id the steward clicked. The payload's crm_account_id
         * field entry - the same lookup normalize does for from-account
         * ownership - covers the rows that carry no id at all.
         */
        const fromPayload = (response?.contact_data ?? []).find(
          (entry) => entry.field_name === "crm_account_id"
        )?.crm_value;
        loadSummary(
          integratedSystemId || response?.integrated_system_id,
          crmAccountId || fromPayload
        );
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, reloadToken, enabled]);

  return {
    summary,
    /** null is a settled failure; undefined is still in flight. */
    failed: summary === null,
    /** The ids actually in play, once explain has reported them. */
    effectiveSystemId: integratedSystemId || resolved.systemId,
    effectiveCrmAccountId: crmAccountId || resolved.crmAccountId,
    reload,
    reloadSilently,
  };
};
